import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBlock } from "../../../store/thunks/blocksThunk";
import {
  BookmarkIcon,
  ReceiptRefundIcon,
  XMarkIcon,
  CheckIcon,
  QuestionMarkCircleIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleBottomCenterTextIcon,
  BanknotesIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";

const CalendarEvent = ({
  appointment,
  onClick,
  onRightClick,
  column,
  maxColumns,
  startHour,
  onAppointmentHover,
  onAppointmentHoverEnd,
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false); // State for tooltip visibility
  const [tooltipPosition, setTooltipPosition] = useState({}); // State for tooltip position
  const eventRef = useRef(null); // Ref to get the bounding rectangle
  const dispatch = useDispatch();
  const [localBlockData, setLocalBlockData] = useState(null);

  useEffect(() => {
    if (appointment?.blockId) {
      dispatch(getBlock(appointment.blockId))
        .then((response) => {
          setLocalBlockData(response.payload);
        })
        .catch((error) => {
          console.error("Failed to fetch block data:", error);
        });
    }
  }, [dispatch, appointment?.blockId]);

  const start = new Date(appointment.start);
  const end = new Date(appointment.end);
  // Calculate offsets
  const startOffset =
    ((start.getHours() - startHour) * 60 + start.getMinutes()) / 5;
  const endOffset = ((end.getHours() - startHour) * 60 + end.getMinutes()) / 5;
  const eventDuration = endOffset - startOffset;

  const eventColor = appointment.clinician?.calendarColor || "#000FFF";

  // Create a string for all statuses for tooltip
  const statusText = Array.isArray(appointment.status)
    ? appointment.status.join(", ")
    : "No Status";

  const isMeeting = appointment.isMeeting;

  const handleMouseEnter = (e) => {
    setIsTooltipVisible(true);
    // Get the bounding rectangle of the event
    const rect = eventRef.current.getBoundingClientRect();
    // Set tooltip position directly above the event
    setTooltipPosition({
      top: rect.top - 30, // 30px above the event
      left: rect.left + rect.width / 2, // Centered horizontally
    });
    
    // Call the appointment hover handler if provided (for non-meetings)
    if (onAppointmentHover && !appointment.isMeeting) {
      onAppointmentHover(appointment, e);
    }
  };

  const handleMouseLeave = () => {
    setIsTooltipVisible(false);
    
    // Call the appointment hover end handler if provided (for non-meetings)
    if (onAppointmentHoverEnd && !appointment.isMeeting) {
      onAppointmentHoverEnd();
    }
  };

  // Convert hex color to rgba with opacity
  const hexToRgba = (hex, opacity) => {
    if (!hex) return `rgba(0,0,0,0)`;

    let r = 0,
      g = 0,
      b = 0;
    if (hex.length === 4) {
      r = parseInt(hex[1] + hex[1], 16);
      g = parseInt(hex[2] + hex[2], 16);
      b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
      r = parseInt(hex[1] + hex[2], 16);
      g = parseInt(hex[3] + hex[4], 16);
      b = parseInt(hex[5] + hex[6], 16);
    }
    return `rgba(${r},${g},${b},${opacity})`;
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    onRightClick(e, appointment);
  };

  const backgroundColor = hexToRgba(eventColor, 0.65);

  return (
    <ul
      ref={eventRef}
      key={appointment._id}
      className="group relative text-white cursor-pointer overflow-hidden rounded-sm border shadow-sm transition-all hover:shadow-md"
      style={{
        backgroundColor: isMeeting ? "#00008B" : hexToRgba(eventColor, 0.65),
        borderColor: isMeeting ? "#fff" : eventColor,
        gridRow: `${startOffset + 2} / span ${eventDuration}`,
        gridColumn: `${column + 1} / span 1`,
        fontSize: "0.75rem",
        zIndex: 20,
      }}
      onClick={() => onClick(appointment)}
      onContextMenu={(e) => {
        e.preventDefault();
        onRightClick(e, appointment);
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      {/* Custom Tooltip */}
      {isTooltipVisible && (
        <div className="absolute z-50 -top-2 left-1/2 transform -translate-x-1/2 -translate-y-full bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
          {statusText}
        </div>
      )}
      <div className="flex h-full flex-col justify-between p-2">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-1">
              {isMeeting ? (
                <CalendarIcon
                  className="h-4 w-4 text-white/90"
                  data-tip="Meeting"
                />
              ) : (
                Array.isArray(appointment.status) &&
                appointment.status.map((status, index) => renderStatusIcon(status, index))
              )}
            </div>
            {isMeeting ? (
              <h3 className="text-sm font-medium leading-none truncate text-white/90">
                {appointment.title}
              </h3>
            ) : (
              appointment.client && (
                <h3 className="text-sm font-medium leading-none truncate text-white/90">
                  {`${appointment.client.firstName} ${appointment.client.lastName}`}
                </h3>
              )
            )}
          </div>
          {!isMeeting && (
            <p className="text-[0.7rem] text-white/75 truncate">
              {appointment.title}
            </p>
          )}
          {isMeeting && appointment.description && (
            <p className="text-[0.7rem] text-white/75 truncate mt-1">
              {appointment.description}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between text-[0.7rem] text-white/75">
          <p>
            {`${start.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })} - ${end.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}`}
          </p>
          <div className="flex items-center gap-2">
            {appointment.resources && appointment.resources.length > 0 && (
              <p className="truncate max-w-[100px]">
                {appointment.resources.map((resource) => resource.name).join(", ")}
              </p>
            )}
            {!isMeeting && appointment.blockId && (
              <p>
                Block {appointment.blockNumber}
                {localBlockData?.appointments?.length && (
                  <span> / {localBlockData.appointments.length}</span>
                )}
              </p>
            )}
          </div>
        </div>
      </div>
    </ul>
  );
};

const renderStatusIcon = (status, index) => {
  let IconComponent = null;
  let colorClass = "";

  switch (status) {
    case "Booked":
      IconComponent = BookmarkIcon;
      colorClass = "text-white/90";
      break;
    case "Changed":
      IconComponent = ReceiptRefundIcon;
      colorClass = "text-white/90";
      break;
    case "Cancelled":
    case "Cancelled - No Fee":
    case "Cancelled - Charge Fee":
    case "Cancelled - Clinician":
    case "Cancelled - Client":
    case "No Show":
    case "No Show - No Fee":
    case "No Show - Charge Fee":
      IconComponent = XMarkIcon;
      colorClass = "text-white/90";
      break;
    case "Treated":
      IconComponent = CheckCircleIcon;
      colorClass = "text-white/90";
      break;
    case "Tentative":
      IconComponent = QuestionMarkCircleIcon;
      colorClass = "text-white/90";
      break;
    case "Invoiced":
      IconComponent = DocumentTextIcon;
      colorClass = "text-white/90";
      break;
    case "Invoice Paid":
      IconComponent = CreditCardIcon;
      colorClass = "text-white/90";
      break;
    case "Soap Notes Saved":
      IconComponent = ClipboardDocumentListIcon;
      colorClass = "text-white/90";
      break;
    case "Soap Notes Completed":
      IconComponent = ClipboardDocumentListIcon;
      colorClass = "text-white/90";
      break;
    case "Soap Notes Updated":
      IconComponent = ClipboardDocumentListIcon;
      colorClass = "text-white/90";
      break;
    case "Soap Notes Approved":
      IconComponent = ClipboardDocumentCheckIcon;
      colorClass = "text-white/90";
      break;
    case "Confirmed":
      IconComponent = CheckIcon;
      colorClass = "text-white/90";
      break;
    case "Comments":
      IconComponent = ChatBubbleBottomCenterTextIcon;
      colorClass = "text-white/90";
      break;
    case "Paid to Clinician":
      IconComponent = BanknotesIcon;
      colorClass = "text-white/90";
      break;
    case "Completed":
      IconComponent = CheckBadgeIcon;
      colorClass = "text-white/90";
      break;
    default:
      return null;
  }

  return (
    <div className="relative" key={`${status}-${index}`}>
      <IconComponent className={`h-4 w-4 ${colorClass}`} />
    </div>
  );
};

export default CalendarEvent;
